import { useId, useCallback, useEffect } from "react";
import { shortReference } from "../../../shared/references/shortReferences";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { appPaths } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { connectionStatus, latestUpdateRequest } from "./connectionState";
import { upstreamLink } from "./upstreamLink";
import { listSourceConnections, requestSourceUpdate, type ConnectionStatusView } from "./sourceConnectionsApi";

interface Props {
  readonly projectId: string;
  readonly mayUpdate: boolean;
  readonly corpusNames: ReadonlyMap<string, string>;
}

/**
 * The project's source connections, independent of the corpus selected for reading. Hidden
 * from a reader who can't add one while there are none (W-09, P-B).
 */
export default function SourceConnections({ projectId, mayUpdate, corpusNames }: Props) {
  const { t } = useTranslation();
  const headingId = useId();
  const load = useCallback((signal: AbortSignal) => listSourceConnections(projectId, signal), [projectId]);
  const { state, reload, refresh } = useLoad(load);
  useEffect(() => {
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 5000);
    return () => { window.clearInterval(timer); };
  }, [refresh]);

  if (!mayUpdate && state.status === "loaded" && state.value.length === 0) return null;
  return (
    <section className="space-y-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-lg font-semibold">{t("sources.connections.title")}</h3>
      {state.status === "loading" ? <p role="status" className="app-text-muted text-sm">{t("sources.connections.loading")}</p> : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} onRetry={reload} /> : null}
      {state.status === "loaded" && state.value.length === 0 ? <p className="app-text-muted text-sm">{t("sources.connections.empty")}</p> : null}
      {state.status === "loaded" ? <ul className="space-y-3">{state.value.map((view) => (
        <ConnectionRow key={`${view.connection.sourceId}/${view.connection.connectionId}`} view={view}
          corpusName={corpusNames.get(view.connection.corpusId) ?? view.connection.corpusId} mayUpdate={mayUpdate} refresh={refresh} />
      ))}</ul> : null}
    </section>
  );
}

function ConnectionRow({ view, corpusName, mayUpdate, refresh }: {
  readonly view: ConnectionStatusView; readonly corpusName: string; readonly mayUpdate: boolean; readonly refresh: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const { connection, thread } = view;
  const status = connectionStatus(connection, thread);
  const request = latestUpdateRequest(connection);
  const label = t(`sources.connections.states.${status}`);
  function commitLabel(commit: string | null, date: string | null) {
    return commit === null ? t("sources.connections.none") : `${shortReference("gitObject", commit)}${date === null ? "" : ` (${formatDateTime(date, i18n.resolvedLanguage)})`}`;
  }
  const upstream = connection.sourceRemote === null ? null : upstreamLink(connection.sourceRemote);
  return (
    <li className="app-card space-y-3 p-4 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="app-title break-words font-semibold">{connection.sourceName}</h4>
        <span className="app-text-muted break-all">{t("sources.connections.corpus", { name: corpusName })}</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          {upstream === null ? (
            <span className="break-all">{connection.sourceRemote ?? t("sources.connections.uploaded")}</span>
          ) : (
            <a className="app-link break-all underline" href={upstream.href} target="_blank" rel="noreferrer">{upstream.label}</a>
          )}
          <span role="status">{request?.thread ? <Link className="app-link underline" to={appPaths.thread(request.thread)}>{label}</Link> : label}</span>
        </p>
        {(status === "update_available" || status === "up_to_date") && mayUpdate ? <AppButton size="sm" disabled={state.status === "submitting"}
          onClick={() => { submit(async () => { await requestSourceUpdate(connection); refresh(); }); }}>{t("sources.connections.updateNow")}</AppButton> : null}
      </div>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <details className="app-text-muted">
        <summary className="cursor-pointer list-item">{t("sources.connections.details")}</summary>
        <div className="mt-2 space-y-2">
          <p className="break-words">{t("sources.connections.versions", {
            upstream: commitLabel(connection.upstream, connection.upstreamAt),
            imported: commitLabel(connection.imported, connection.importedAt) + (connection.importedSelection == null
              ? "" : ` (${t("sources.connections.sample", { count: connection.importedSelection.length })})`),
          })}</p>
          <dl className="flex flex-wrap gap-x-6 gap-y-2">
            <div><dt className="inline">{t("sources.connections.policy")}: </dt><dd className="inline">{t(`sources.connections.policies.${connection.policy}`)}</dd></div>
            <div className="min-w-0 break-all"><dt className="inline">{t("sources.connections.host")}: </dt><dd className="inline" title={connection.host}>{connection.hostName ?? connection.host}</dd></div>
            <div><dt className="inline">{t("sources.connections.branch")}: </dt><dd className="inline">{connection.branch}</dd></div>
          </dl>
        </div>
      </details>
    </li>
  );
}
